import { useQuery } from "@tanstack/react-query";
import axios from "axios";

/**
 * The branding themes (letterheads) configured on a Xero organisation.
 *
 * Themes are per organisation, so the list is keyed on the org selected in the
 * PAYMENTS section and re-fetched when that changes — a theme from one org
 * cannot be stamped on a document in another.
 *
 * The first entry is flagged `is_default`: it is the theme the backend has
 * always used when nothing is pinned, so a document with no explicit pick comes
 * out exactly as it did before this picker existed.
 *
 * The endpoint never fails loudly — any Xero problem returns an empty list, and
 * the picker then hides itself rather than blocking the invoice.
 */
export const useXeroBrandingThemes = (xeroConnectionId, enabled = true) =>
  useQuery({
    queryKey: ["xero-branding-themes", xeroConnectionId ?? null],
    queryFn: async () => {
      const { data } = await axios.get("/api/xero/branding-themes", {
        params: xeroConnectionId
          ? { xero_connection_id: xeroConnectionId }
          : {},
      });
      return data?.themes || [];
    },
    enabled,
    // Themes change when someone edits them in Xero, which is rare — but not
    // never, so this expires rather than caching for the session.
    staleTime: 5 * 60 * 1000,
  });

/** Pin which branding theme an ORDER's Xero invoice is generated with. */
export const setOrderXeroBrandingTheme = async (orderId, themeId) => {
  const { data } = await axios.post(
    `/api/order-management/${orderId}/xero-branding-theme`,
    { xero_branding_theme_id: themeId }
  );
  return data;
};

/**
 * Pin the branding theme and/or billing address a SELL REQUEST's purchase order
 * is generated with. Pass only the keys being changed.
 *
 * A dedicated narrow endpoint, NOT `PUT /api/requests/{id}`: that route used to
 * rebuild the whole record from the payload, so a partial update cleared
 * `year`, `price_expectation` / `delivery_set` and forced `is_direct` (it now
 * writes only the keys sent, L10). Picking a dropdown must not touch the deal.
 */
export const setRequestXeroOptions = async (requestId, options) => {
  const { data } = await axios.post(
    `/api/requests/${requestId}/xero-options`,
    options
  );
  return data;
};
